import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { getSubmissions } from "@/lib/api";
import { btnPrimary, inputCls } from "@/lib/site";

const BADGE = {
  orders: "bg-sky-100 text-sky-700",
  quotes: "bg-amber-100 text-amber-700",
  messages: "bg-emerald-100 text-emerald-700",
};

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

function Table({ type, rows, cols }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
      <table className="w-full min-w-[760px] text-left text-sm">
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50">
            {cols.map((c) => (
              <th key={c} className="px-4 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={cols.length} className="px-4 py-8 text-center text-xs text-slate-400">
                No {type} yet.
              </td>
            </tr>
          )}
          {rows.map((r) => (
            <tr key={r.ref} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
              <td className="px-4 py-3">
                <span className={`rounded px-2 py-0.5 font-mono text-[10px] uppercase ${BADGE[type]}`}>{r.ref}</span>
              </td>
              <td className="px-4 py-3 font-medium text-slate-900">{r.name}</td>
              <td className="px-4 py-3 text-slate-600">{r.email}<br /><span className="font-mono text-xs">{r.phone}</span></td>
              <td className="px-4 py-3 text-slate-600">
                {type === "orders" ? (
                  <> <span className="font-medium text-slate-900">{r.product_name}</span> × {r.quantity}<br />→ {r.delivery_location}</>
                ) : type === "quotes" ? (
                  <> <span className="font-medium text-slate-900">{r.product_name}</span>{r.quantity ? ` · ${r.quantity}` : ""}{r.message ? <><br />{r.message}</> : ""}</>
                ) : (
                  <> {r.subject ? <span className="font-medium text-slate-900">{r.subject}</span> : null}{r.message ? <><br />{r.message}</> : ""}</>
                )}
              </td>
              <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-400">{fmt(r.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Admin() {
  usePageMeta("Admin | Krithish Scientific Company", "Submissions dashboard.");
  const [key, setKey] = useState(sessionStorage.getItem("lt_admin") || "");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async (e) => {
    if (e) e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const d = await getSubmissions(key);
      setData(d);
      sessionStorage.setItem("lt_admin", key);
    } catch {
      setErr("Invalid admin key. Check the ADMIN_KEY in the backend .env file.");
      setData(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="min-h-[70vh] bg-slate-50 py-16" data-testid="admin-page">
      <div className="mx-auto max-w-7xl px-6">
        {!data ? (
          <form onSubmit={load} className="mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
            <span className="inline-flex rounded-lg bg-sky-50 p-3 text-safety">
              <KeyRound size={20} />
            </span>
            <h1 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900">Admin — Submissions</h1>
            <p className="mt-1.5 text-sm text-slate-500">Enter the admin key to view orders, price enquiries and messages.</p>
            <input
              data-testid="admin-key-input"
              className={`${inputCls} mt-5`}
              type="password"
              placeholder="Admin key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
            />
            {err && <p className="mt-2 text-xs font-medium text-red-600">{err}</p>}
            <button data-testid="admin-load-btn" className={`${btnPrimary} mt-4 w-full`} disabled={busy || !key}>
              {busy ? <Loader2 size={16} className="animate-spin" /> : null}
              {busy ? "Checking…" : "View Submissions"}
            </button>
          </form>
        ) : (
          <div className="space-y-10">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Submissions</h1>
                <p className="mt-1 text-sm text-slate-500">
                  {data.orders.length} orders · {data.quotes.length} price enquiries · {data.messages.length} messages
                </p>
              </div>
              <button onClick={load} className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 transition hover:border-safety hover:text-safety">
                Refresh
              </button>
            </div>
            <div>
              <h2 className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Orders</h2>
              <Table type="orders" rows={data.orders} cols={["Ref", "Name", "Contact", "Product", "Date"]} />
            </div>
            <div>
              <h2 className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Price Enquiries</h2>
              <Table type="quotes" rows={data.quotes} cols={["Ref", "Name", "Contact", "Product", "Date"]} />
            </div>
            <div>
              <h2 className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Messages</h2>
              <Table type="messages" rows={data.messages} cols={["Ref", "Name", "Contact", "Message", "Date"]} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
